import { createHouseSurvey as apiCreateHouseSurvey, ApiError } from './apiService';

/**
 * Cola local + envío para las respuestas de un paso del flujo de una
 * vivienda (`POST /v1/houses/:houseId/surveys/:surveySlug`), ej. el
 * formulario "conversation" que se abre después de una visita con
 * `visit_result: 'conversation'`. Sigue el mismo patrón offline-first
 * que `houseCaptureService.js`/`visitService.js`.
 */

const QUEUE_KEY = 'pulso17.houseSurveys.queue';
export const MAX_QUEUED_HOUSE_SURVEYS = 10;

/** Error específico para cuando la cola local ya llegó al máximo permitido. */
export class HouseSurveyQueueFullError extends Error {
  constructor() {
    super(
      `Ya tienes ${MAX_QUEUED_HOUSE_SURVEYS} formularios guardados sin sincronizar. Sincroniza antes de capturar otro.`
    );
    this.name = 'HouseSurveyQueueFullError';
  }
}

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

/** Respuestas de pasos de flujo guardadas localmente, pendientes de sincronizar. */
export function getQueuedHouseSurveys() {
  return readQueue();
}

/** Cantidad de respuestas pendientes (para badges/KPIs). */
export function getQueuedHouseSurveysCount() {
  return readQueue().length;
}

/**
 * Arma el objeto a encolar para `POST /v1/houses/:houseId/surveys/:surveySlug`.
 *
 * @param {{ houseId: string|number, surveySlug: string, answersByKey: Record<string, any> }} params
 */
export function buildHouseSurveyPayload({ houseId, surveySlug, answersByKey }) {
  return {
    _houseId: Number(houseId),
    _surveySlug: surveySlug,
    answers: answersByKey,
  };
}

/**
 * Guarda una respuesta en la cola local. Lanza
 * `HouseSurveyQueueFullError` si ya se alcanzó `MAX_QUEUED_HOUSE_SURVEYS`.
 *
 * @param {Object} houseSurvey ver `buildHouseSurveyPayload`.
 */
export function enqueueHouseSurvey(houseSurvey) {
  const queue = readQueue();
  if (queue.length >= MAX_QUEUED_HOUSE_SURVEYS) {
    throw new HouseSurveyQueueFullError();
  }
  writeQueue([...queue, houseSurvey]);
  return houseSurvey;
}

/**
 * Sincroniza todas las respuestas pendientes, una por una, contra
 * `POST /v1/houses/:houseId/surveys/:surveySlug`. Las que se envían
 * con éxito se quitan de la cola; si alguna falla, se detiene y la
 * cola conserva los pendientes (incluida la que falló) para
 * reintentar después.
 *
 * @returns {Promise<{ synced: number }>}
 */
export async function syncQueuedHouseSurveys() {
  const queue = readQueue();
  let synced = 0;
  const remaining = [...queue];

  while (remaining.length > 0) {
    const item = remaining[0];
    try {
      const { _houseId: houseId, _surveySlug: surveySlug, ...payload } = item;
      await apiCreateHouseSurvey(houseId, surveySlug, payload);
      remaining.shift();
      synced += 1;
      writeQueue(remaining);
    } catch (err) {
      writeQueue(remaining);
      throw err;
    }
  }

  return { synced };
}

/** `true` si el error viene de que el backend no está disponible. */
export function isNetworkError(err) {
  return err instanceof ApiError && err.status === 0;
}
